/***
 *
 *   DASHBOARD
 *   Template dashboard example demonstrating various components inside a view.
 *
 **********/

import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Card,
  Chart,
  Table,
  Message,
  Grid,
  Button,
  Animate,
  useNavigate,
  Feedback,
  useAPI,
  ViewContext,
  Paginate,
  Badge,
  usePersistedTableState,
  STATE_HANDLERS,
} from "components/lib";
import axios from "axios";
import "./dashboard.scss";
import MaterialReactTable from "material-react-table";
import { Box } from "@mui/material";
import { mkConfig, download, generateCsv } from "export-to-csv";
import { tableStateKey, buildExportRow } from "./dashboard-table";
import {
  derivePepResult,
  deriveSanctionsResult,
  deriveOtherScreeningResult,
  deriveAdverseMediaResult,
  SCREENING_FILTER_OPTIONS,
  SCREENING_YES,
  SCREENING_NO,
  SCREENING_NA,
} from "../verification/screening-summary";

// Tranche 2 risk_assessment.status -> dashboard badge label + colour.
const RA_STATUS_META = {
  consent_pending: { label: "Consent pending", color: "orange" },
  consent_declined: { label: "Consent declined", color: "red" },
  awaiting_intake: { label: "Awaiting intake", color: "orange" },
  awaiting_biometric: { label: "Awaiting biometric", color: "orange" },
  awaiting_review: { label: "Awaiting review", color: "blue" },
  in_review: { label: "In review", color: "blue" },
  completed: { label: "Completed", color: "green" },
  cancelled: { label: "Cancelled", color: "red" },
  failed: { label: "Failed", color: "red" },
};

// Yes/No -> badge colour for the four screening columns. N/A deliberately has
// no entry: Badge only styles red/blue/green/orange (badge.tailwind.js), and an
// unmapped colour falls through to base-only classes, i.e. white text on a
// white background. It renders as the same grey text the risk assessment
// column already uses for its N/A.
const SCREENING_BADGE_COLOR = {
  [SCREENING_YES]: "red",
  [SCREENING_NO]: "green",
};

// Shared by all four screening columns. Reads the accessorFn's output rather
// than the row, so sorting, filtering, grouping and the CSV export all keep
// operating on the raw Yes/No/N/A strings.
function ScreeningCell({ cell }) {
  const value = cell.getValue();
  const color = SCREENING_BADGE_COLOR[value];

  if (!color)
    return <span className="text-slate-400">{value || SCREENING_NA}</span>;

  return <Badge text={value} color={color} />;
}

// Bump when the persisted shape changes. Renamed column ids do not need a bump
// -- the hook drops sorting/filter/grouping entries naming columns the table no
// longer has, and merges columnVisibility per id.
const TABLE_STATE_VERSION = 1;

// Hidden on a first visit. Persistence is what makes revealing them stick
// across a trip out to a verification and back.
const HIDDEN_BY_DEFAULT_COLUMNS = [
  "month",
  "created_at",
  "type",
  "source",
  "has_pep_results",
  "has_sanction_results",
  "has_other_screening_results",
  "has_adverse_media_results",
  "risk_assessment",
];

// The first-visit view, and the declaration of which state keys persist at all.
const DEFAULT_TABLE_STATE = {
  columnVisibility: Object.fromEntries(
    HIDDEN_BY_DEFAULT_COLUMNS.map((id) => [id, false])
  ),
  sorting: [{ id: "created_at", desc: true }],
  columnFilters: [],
  globalFilter: "",
  // pageSize persists reliably; pageIndex is best-effort. TanStack wires
  // _autoResetPageIndex to the filtered/sorted/grouped row models, so changing
  // a filter still snaps back to page 1 — which is what we want, and why
  // autoResetPageIndex is deliberately left on. Its first call is swallowed by
  // an internal `registered` guard, so a restored page survives mount.
  pagination: { pageIndex: 0, pageSize: 10 },
  grouping: [],
  density: "comfortable",
  showColumnFilters: false,
  showGlobalFilter: false,
};

const CSV_CONFIG = mkConfig({
  fieldSeparator: ",",
  decimalSeparator: ".",
  useKeysAsHeaders: true,
});

export function Dashboard(props) {
  const [verifiCationData, setVerificationData] = useState([]);
  const [verificationChart, setVerificationChart] = useState({});
  // const stats = useAPI("/api/demo/stats");
  // const progress = useAPI("/api/demo/progress");
  // const table = useAPI("/api/demo/users/list");
  // const userChart = useAPI("/api/demo/users/types");
  // const revenueChart = useAPI("/api/demo/revenue");

  const [reRender, setReRender] = useState(1);
  const [offset, setOffset] = useState(0);
  const limit = 10;
  const currentUser = useAPI("/api/user");
  const verifications = useAPI("/api/verification/list");
  // Rolling last 12 months, aggregated server-side (model/verification.js getChart),
  // labelled MM/YYYY. Datasets arrive as Total, Negative, Positive.
  const last12monthChartData = useAPI("/api/verification/chart");
  const context = useContext(ViewContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (verifications?.data?.verification?.length === 0) {
      return (window.location = "/landing");
    }
    // chartData();
  }, [verifications]);

  const chartData = () => {
    let data = verifications?.data?.verification;
    let total = [];
    let positive = [];
    let negative = [];
    if (data) {
      total = data;
      positive = data?.filter((x) => x.result?.toLowerCase() === "positive");
      negative = data?.filter((x) => x.result?.toLowerCase() === "negative");

      const labels = [` Total`, `Positive`, "Negative"];
      const months = [
        { label: "jan", monthNumber: 1 },
        { label: "feb", monthNumber: 2 },
        { label: "mar", monthNumber: 3 },
        { label: "apr", monthNumber: 4 },
        { label: "may", monthNumber: 5 },
        { label: "jun", monthNumber: 6 },
        { label: "jul", monthNumber: 7 },
        { label: "aug", monthNumber: 8 },
        { label: "sep", monthNumber: 9 },
        { label: "oct", monthNumber: 10 },
        { label: "nov", monthNumber: 11 },
        { label: "dec", monthNumber: 12 },
      ];

      let monthWisePositiveData = [];
      let monthWiseNegativeData = [];
      let monthWiseTotalData = [];
      months?.map((x, index) => {
        let totalRecordsOfMonth = data?.filter(
          (y) => new Date(y?.created_at)?.getMonth() + 1 === x.monthNumber
        )?.length;
        let negativeRecordsOfMonth = data?.filter(
          (y) =>
            y.result?.toLowerCase() === "negative" &&
            new Date(y?.created_at)?.getMonth() + 1 === x.monthNumber
        )?.length;
        let positiveRecordsOfMonth = data?.filter(
          (y) =>
            y.result?.toLowerCase() === "positive" &&
            new Date(y?.created_at)?.getMonth() + 1 === x.monthNumber
        )?.length;

        monthWiseTotalData.push({
          labels: x.label,
          value: totalRecordsOfMonth,
        });

        monthWisePositiveData.push({
          labels: x.label,
          value: positiveRecordsOfMonth,
        });

        monthWiseNegativeData.push({
          labels: x.label,
          value: negativeRecordsOfMonth,
        });
      });
      const datasets = [
        monthWiseTotalData,
        monthWisePositiveData,
        monthWiseNegativeData,
      ];
      if (!Array.isArray(datasets[0])) datasets = [datasets];

      if (!Array.isArray(labels)) labels = [labels];

      let chart = {
        labels: null,
        datasets: [],
      };
      // for each dataset
      if (datasets) {
        for (let i = 0; i < datasets.length; i++) {
          let values = [],
            ticks = [];

          for (let j = 0; j < datasets[i].length; j++) {
            ticks.push(datasets[i][j].labels);
            values.push(datasets[i][j].value);
          }

          chart.labels = ticks;
          chart.datasets.push({
            label: labels[i],
            data: values,
          });
        }
      }

      setVerificationChart(chart);
    }
  };
  async function makeActive(data) {
    try {
      await axios({
        url: `/api/verification/${data.id}`,
        method: "patch",
        data: { active: true },
      });
      context.notification.show(`User activated`, "success", true);
      setReRender(reRender + 1);
    } catch (err) {
      context.handleError(err);
    }
  }
  const makeInActive = async (data) => {
    try {
      await axios({
        url: `/api/verification/${data.id}`,
        method: "patch",
        data: { active: false },
      });
      context.notification.show(`User in-activate`, "success", true);
      setReRender(reRender + 2);
    } catch (err) {
      context.handleError(err);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "first_name", //access nested data with dot notation
        header: "First Name",
      },
      {
        accessorKey: "created_at",
        header: "Created At",
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
      },
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "result",
        header: "result",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          switch (value?.toLowerCase()) {
            case "positive":
              return <Badge text="Positive" color="green" />;
            case "negative":
              return <Badge text="Negative" color="red" />;
            case "in progress":
              return <Badge text="In progress" color="orange" />;
            default:
              return <Badge text={value || "—"} color="blue" />;
          }
        },
      },
      {
        accessorFn: (row) => {
          const date = new Date(row.created_at);
          return `${date.getMonth() + 1}-${date.getFullYear()}`;
        },
        id: "month",
        header: "Month",
      },
      {
        accessorFn: (row) => {
          const date = new Date(row.created_at);
          return `${date.getFullYear()}-${
            date.getMonth() + 1
          }-${date.getDate()}`;
        },
        id: "date",
        header: "Date",
      },
      {
        accessorFn: (row) => {
          const type = row.type.split("+").join(", ");
          return type;
        },
        id: "type",
        filterVariant: "multi-select",
        filterSelectOptions: [
          "kyc",
          "kyc, sanctions",
          "kyc, sanctions_monitoring",
          "kyc, adverse_media",
          "kyc, sanctions, adverse_media",
          "sanctions_monitoring",
        ],
        header: "Type",
      },
      {
        id: "source",
        header: "Source",
        filterVariant: "select",
        filterSelectOptions: ["Public form", "Staff / Integration"],
        // Feature 008: distinguish verifications a client self-started via the
        // public intake link from staff/integration-initiated ones (FR-013).
        accessorFn: (row) =>
          row.triggered_by === "public_intake" ? "Public form" : "Staff / Integration",
      },
      // PEP and Sanctions share one stored result (sanctions_results) but are
      // materially different findings, so they get a column each, split by each
      // found_record's source_type. CRIMINAL/OTHER (and any unrecognised value)
      // get the third column rather than being dropped. All three derive from
      // found_records — the same signal as the detail views, the PDF, the
      // account AML CSV and the HubSpot writeback — not the raw total_hits
      // scalar the single combined column used to read.
      {
        id: "has_pep_results",
        header: "PEP Results",
        filterVariant: "select",
        filterSelectOptions: SCREENING_FILTER_OPTIONS,
        accessorFn: derivePepResult,
        Cell: ScreeningCell,
      },
      {
        id: "has_sanction_results",
        header: "Sanctions Results",
        filterVariant: "select",
        filterSelectOptions: SCREENING_FILTER_OPTIONS,
        accessorFn: deriveSanctionsResult,
        Cell: ScreeningCell,
      },
      {
        id: "has_other_screening_results",
        header: "Other Screening Results",
        filterVariant: "select",
        filterSelectOptions: SCREENING_FILTER_OPTIONS,
        accessorFn: deriveOtherScreeningResult,
        Cell: ScreeningCell,
      },
      {
        id: "has_adverse_media_results",
        header: "Adverse Media Results",
        filterVariant: "select",
        filterSelectOptions: SCREENING_FILTER_OPTIONS,
        // Mirrors the PEP/Sanctions columns, but adverse_media_results has a
        // different shape (the raw iDenfy AML response, no found_records), so
        // the derivation lives beside its siblings in screening-summary rather
        // than inline here — the CSV export below reuses the same helper.
        accessorFn: deriveAdverseMediaResult,
        Cell: ScreeningCell,
      },
      {
        id: "risk_assessment",
        header: "Tranche 2 Risk Assessment",
        filterVariant: "select",
        filterSelectOptions: [
          "consent_pending",
          "consent_declined",
          "awaiting_intake",
          "awaiting_biometric",
          "awaiting_review",
          "in_review",
          "completed",
          "cancelled",
          "failed",
        ],
        // Sortable/filterable primitive. Must never be null: MRT calls
        // .toString() on cell values while filtering/faceting, so non-tranche2
        // rows (and tranche2 rows without a risk assessment yet) return "N/A"
        // — same as the PEP/Sanctions column above.
        accessorFn: (row) =>
          row.type?.includes("tranche2_v1") && row.risk_assessment
            ? row.risk_assessment.status
            : "N/A",
        Cell: ({ row }) => {
          const v = row.original;
          const ra = v.risk_assessment;
          if (!v.type?.includes("tranche2_v1") || !ra) {
            return <span className="text-slate-400">N/A</span>;
          }
          const meta = RA_STATUS_META[ra.status] || {
            label: ra.status || "Unknown",
            color: "blue",
          };
          return <Badge text={meta.label} color={meta.color} />;
        },
      },
    ],
    []
  );

  // Every id the table actually has. Restored sorting / filters / grouping
  // naming anything else are dropped rather than handed to MRT, whose grouping
  // banner dereferences table.getColumn(id).columnDef unguarded.
  const columnIds = useMemo(
    () => columns.map((c) => c.id || c.accessorKey).filter(Boolean),
    [columns]
  );

  const storageKey = useMemo(
    () => tableStateKey(JSON.parse(localStorage.getItem("user") || "null")),
    []
  );

  const [tableState, tableHandlers] = usePersistedTableState(
    storageKey,
    DEFAULT_TABLE_STATE,
    TABLE_STATE_VERSION,
    columnIds
  );

  // Persistence makes a filtered-down view outlive the page, so there has to be
  // one obvious way back to the default view.
  const resetTableView = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch (err) {
      // Storage unavailable; resetting the in-memory state below still works.
    }
    Object.entries(DEFAULT_TABLE_STATE).forEach(([key, value]) => {
      const handler = tableHandlers[STATE_HANDLERS[key]];
      if (handler) handler(value);
    });
  };

  const csvConfig = CSV_CONFIG;

  /*
   * Exports what the table is showing: filters and sorting are honoured,
   * pagination is not, so the file matches the visible result set rather than
   * the current page. Reading the row model rather than the raw API payload
   * matters now that filters persist -- an operator could otherwise see three
   * rows on screen and download five thousand.
   */
  const handleExportData = (table) => {
    const rows = table
      .getPrePaginationRowModel()
      .rows.map((row) => buildExportRow(row.original));

    // export-to-csv reads data[0] to build the header row.
    if (!rows.length) {
      context.notification.show("Nothing to export", "error", true);
      return;
    }

    download(csvConfig)(generateCsv(csvConfig)(rows));
  };

  console.log("veri", verifications.data);
  return (
    <Animate type="pop">
      {currentUser.data?.tranche2_risk_assessment_enabled_for_user === true &&
        !currentUser.data?.risk_assessment_pin_set && (
          <Message
            title="Set your Risk Assessment PIN"
            type="warning"
            text="You can complete tranche 2 risk assessments on this account, but your reviewer PIN has not been generated yet."
            buttonText="Set PIN"
            buttonLink="/account/password"
          />
        )}

      <Message
        buttonText="Refer a friend and get $250 off your next bill"
        buttonLink="/refer"
        noIcon={true}
      />

      {/* <Grid cols='4'>
        // <Stat
        //   loading={ stats?.loading }
        //   value={ stats?.data?.users }
        //   label='users'
        //   icon='users'
        // />
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.active }
          label='active'
          icon='check'
        />
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.churned }
          label='churned'
          icon='refresh-cw'
        />
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.latest }
          label='this month'
          icon='calendar'
          change='5%'
        />
      </Grid> */}

      {/* <Card name='revenues' title='Revenue'>
        <Chart
          type='bar'
          legend
          loading={ revenueChart.loading }
          data={ revenueChart.data }
          color={['red', 'blue']}
        />
      </Card> */}

      <Card name="Verifications" title="Verification">
        {/* <Chart
          type="bar"
          legend
          loading={verificationChart.loading}
          data={verificationChart}
          color={["blue", "green", "red"]}
        /> */}
          <Chart
          type="bar"
          legend
          loading={last12monthChartData.loading}
          data={last12monthChartData.data}
          color={["totalTint", "negTint", "posTint"]}
          />
      </Card>

      {/* <Grid cols="0">
        <Card title="Goals" loading={progress.loading}>
          {progress?.data?.map((item) => {
            return (
              <ProgressBar
                key={item.label}
                label={item.label}
                progress={item.value}
              />
            );
          })}
        </Card> */}
      {/* <Card title='User Types'>
          <Chart
            type='pie'
            legend={ true }
            data={ userChart.data }
            loading={ userChart.loading }
            color='purple'
          />
        </Card> */}
      {/* </Grid> */}

      <Card title="Verifications" last>
        {/* <Table
          key={reRender}
          search
          data={verifications.data?.verification?.slice(offset, limit + offset)}
          loading={verifications.loading}
          show={[
            "first_name",
            "last_name",
            "email",
            "result",
            "created_at",
            "active",
          ]}
          actions={{ active: makeActive, inactive: makeInActive, view: { url: 'verification', col: 'id' } }}
          badge={{ col: "plan", color: "blue" }}
        ></Table>
        <div className="pagination">
          <Paginate
            total={verifications.data?.verification?.length}
            limit={limit}
            offset={offset}
            onChange={(e) => setOffset(e)}
          />
        </div> */}
        {verifications?.data?.verification && (
          // <p>hi</p>
          <>
            {console.log("in here", verifications?.data?.verification)}
            <MaterialReactTable
              title="Verifications"
              // theme={theme}
              data={verifications?.data?.verification}
              columns={columns}
              enableRowActions
              enableGrouping
              // Controlled, not initialState: MRT ignores initialState for any
              // key `state` owns, and this state has to outlive the table. The
              // dashboard unmounts on every trip out to a verification and back.
              // A useAPI refetch would do the same, since the guard above hides
              // the table whenever `data` is momentarily blank.
              state={tableState}
              {...tableHandlers}
              positionActionsColumn="last"
              renderRowActions={(row, index) => [
                <Button
                  action={(row) => {
                    console.info("View", row);
                    navigate("/verification/" + row.row.original.id);
                  }}
                  params={row}
                  text="View"
                  outline
                  small
                />,
              ]}
              positionToolbarAlertBanner="bottom"
              renderTopToolbarCustomActions={({ table }) => (
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    p: "0.5rem",
                    flexWrap: "wrap",
                    align: "right",
                  }}
                >
                  <Button
                    // Exports the filtered/sorted rows, across all pages.
                    action={() => handleExportData(table)}
                    text=""
                    icon="download"
                    small
                    key="export"
                  />
                  <Button
                    action={resetTableView}
                    text="Reset view"
                    small
                    outline
                    key="reset-view"
                  />
                </Box>
              )}
            />
          </>
        )}
      </Card>

      <Feedback />
    </Animate>
  );
}
